import React, { createContext, useState, useContext, useEffect, useMemo } from "react";
import axios from "axios";
import { getToken } from "../utils/tokenUtils";

// Create the ApproverModuleMasterContext
const ApproverModuleMasterContext = createContext();

// Custom hook to use the ApproverModuleMasterContext
export const useApproverModule = () => useContext(ApproverModuleMasterContext);

// Provider component to wrap the app
export const ApproverModuleMasterProvider = ({ children }) => {
  const [approverModules, setApproverModules] = useState([]);
  const API_URL = "https://hrmspayroll.prepstripe.com/api/masters/approval-modules"; 
  const token = getToken();
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 10,
  });
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchApproverModules = async (page = 1) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}?page=${page}`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      // setDepartments(response.data?.data || {}); // Access `data.data` for actual approver module master
      const { data, current_page, last_page, per_page, total } = response.data;

      setApproverModules(data.data || []); // Update leave types list

      setPagination({
          currentPage: current_page,
          totalPages: last_page,
          totalItems: total,
          perPage: per_page,
      });
      setLoading(false);
    } catch (error) {
      console.error("Error fetching approver module master:", error);
      setError(error.response?.data?.message || "Failed to fetch approver module.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch departments master from the API on load
  useEffect(() => {
    fetchApproverModules();
  }, [API_URL, token]);

  // Add a new approver module
  const addApproverModule = async (newApproverModule) => {
    try {
      const response = await axios.post(API_URL, newApproverModule, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      // setApproverModules((prev) => [...prev, response.data?.data]);
      setApproverModules((prev) => [response.data?.data, ...prev]);
    } catch (error) {
      console.error("Error adding approver module:", error);
      throw error;
    }
  };

  // Update an existing approver module
  const updateApproverModule = async (updatedApproverModule) => {
    try {
      const response = await axios.put(`${API_URL}/${updatedApproverModule.id}`, updatedApproverModule, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      setApproverModules((prev) =>
        prev.map((approverModule) =>
          approverModule.id === updatedApproverModule.id ? { ...approverModule, ...response.data?.data } : approverModule
        )
      );
    } catch (error) {
      console.error("Error updating approver Module master:", error);
      throw error;
    }
  };

  // Delete a approver module
  const deleteApproverModule = async (id) => {
    try {
      await axios.delete(`${API_URL}/${id}`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      setApproverModules((prev) => prev.filter((approverModule) => approverModule.id !== id));
    } catch (error) {
      console.error("Error deleting approver Module:", error);
      throw error;
    }
  };

  // Memoize the context value
  const value = useMemo(
    () => ({
      isLoading,
      error,
      pagination,
      approverModules,
      addApproverModule,
      updateApproverModule,
      fetchApproverModules,
      deleteApproverModule,
    }),
    [approverModules, isLoading] 
  );

  return <ApproverModuleMasterContext.Provider value={value}>{children}</ApproverModuleMasterContext.Provider>;
};
